import { useEffect, useRef, useState } from 'react';
import type { LatLng } from '../game/geo';
import { MOBILE_QUERY, useMediaQuery } from '../hooks/useMediaQuery';
import { IconClose, IconExpand, IconLock, IconMap, IconPin, IconShrink, IconUnlock } from './Icons';
import MapView from './map/MapView';

interface GuessPanelProps {
  mapKind: 'leaflet' | 'google';
  initialView: { center: LatLng; zoom: number };
  guess: LatLng | null;
  onPick: (position: LatLng) => void;
  onSubmit: () => void;
}

type Size = 'm' | 'l';

/**
 * Guess map: a corner panel that grows on hover on desktop, a full-screen sheet on phones.
 */
export default function GuessPanel({ mapKind, initialView, guess, onPick, onSubmit }: GuessPanelProps) {
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const [active, setActive] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [size, setSize] = useState<Size>('m');
  const [sheetOpen, setSheetOpen] = useState(false);
  const leaveTimer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(leaveTimer.current), []);

  const expanded = isMobile ? sheetOpen : active || pinned;
  const onEnter = () => {
    window.clearTimeout(leaveTimer.current);
    setActive(true);
  };
  const onLeave = () => {
    window.clearTimeout(leaveTimer.current);
    leaveTimer.current = window.setTimeout(() => setActive(false), 700);
  };

  const submit = () => {
    if (!guess) return;
    setSheetOpen(false);
    onSubmit();
  };

  return (
    <>
      {isMobile && !sheetOpen && (
        <button type="button" className="map-fab" onClick={() => setSheetOpen(true)} aria-label="Ouvrir la carte pour deviner">
          <IconMap size={22} />
          <span>{guess ? 'Carte · prêt' : 'Carte'}</span>
        </button>
      )}

      <section
        className={
          isMobile
            ? `guess-panel guess-panel--mobile ${sheetOpen ? '' : 'guess-panel--hidden'}`
            : `guess-panel guess-panel--${size} ${expanded ? 'guess-panel--expanded' : ''}`
        }
        onMouseEnter={isMobile ? undefined : onEnter}
        onMouseLeave={isMobile ? undefined : onLeave}
        onFocus={isMobile ? undefined : onEnter}
        aria-label="Carte pour placer votre réponse"
      >
        <div className="guess-panel__toolbar">
          {isMobile ? (
            <>
              <span className="guess-panel__title">
                <IconPin size={16} /> Touchez la carte pour placer votre marqueur
              </span>
              <button type="button" className="icon-btn icon-btn--small" onClick={() => setSheetOpen(false)} aria-label="Fermer la carte">
                <IconClose size={18} />
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="icon-btn icon-btn--small"
                onClick={() => setSize('l')}
                disabled={size === 'l'}
                aria-label="Agrandir la carte"
                title="Agrandir la carte"
              >
                <IconExpand size={16} />
              </button>
              <button
                type="button"
                className="icon-btn icon-btn--small"
                onClick={() => setSize('m')}
                disabled={size === 'm'}
                aria-label="Réduire la carte"
                title="Réduire la carte"
              >
                <IconShrink size={16} />
              </button>
              <button
                type="button"
                className={`icon-btn icon-btn--small ${pinned ? 'icon-btn--active' : ''}`}
                onClick={() => setPinned((p) => !p)}
                aria-pressed={pinned}
                aria-label={pinned ? 'Ne plus garder la carte ouverte' : 'Garder la carte ouverte'}
                title={pinned ? 'Ne plus garder la carte ouverte' : 'Garder la carte ouverte'}
              >
                {pinned ? <IconLock size={16} /> : <IconUnlock size={16} />}
              </button>
            </>
          )}
        </div>
        <MapView
          kind={mapKind}
          className="guess-panel__map"
          initialView={initialView}
          guess={guess}
          onPick={onPick}
          resizeKey={`${expanded}-${size}-${sheetOpen}`}
          ariaLabel="Carte du monde : cliquez pour placer votre réponse"
        />
        <button type="button" className="btn btn--primary guess-panel__submit" onClick={submit} disabled={!guess}>
          {guess ? 'Valider ma réponse' : 'Placez votre marqueur sur la carte'}
        </button>
      </section>
    </>
  );
}
